/**
 * Find the lowest valued _number_ from all arguments. Very similar to Math.min, except
 * this function doesn't consider null a number.
 * @param values: Set of values to find the lowest number from
 */
export function lowestNumber(...values: (number | null)[]): number {
  let lowest;
  for (const n of values) {
    // we only want numbers greater than 0, and null is equal to 0 🙄
    if (typeof n !== 'number' || n === 0) continue;
    if (lowest === undefined) lowest = n; // our first number!
    if (n < lowest) lowest = n; // is this value lower than what we've stored previously?
  }
  // default to zero if lowest is undefined
  return lowest || 0;
}

/**
 * Parse a string to a float, e.g. '$1,000.00` becomes 1000.00
 * @param value {string}
 * @returns {number}
 */
export function parseStringToFloat(value: string): number {
  return parseFloat(
    value
      .trim()
      .replace(/[A-Za-z]/g, '')
      .replace(/[$,]/g, '')
  );
}

/**
 * Parse a string to a integer, e.g. '#1000` becomes 1000
 * @param value {string}
 * @returns {number}
 */
export function parseStringToInt(value: string): number {
  return parseInt(
    value
      .trim()
      .replace(/[A-Za-z]/g, '')
      .replace(/[$,]/g, '')
  );
}

/**
 * Filter the properties of an object by a prefix and a index(optional), creating and returning
 * a new one with the filtered keys.
 * @param object
 * @param prefix
 * @param index
 * @returns {object}
 */
export function filterObjectPropertiesByNamePrefixAndIndex(
  object: { [k: string]: unknown },
  prefix: string,
  index?: number
): { [k: string]: unknown } {
  const newObject: { [k: string]: unknown } = {};
  for (const [key, value] of Object.entries(object)) {
    if (key.startsWith(prefix)) newObject[key] = index !== undefined && value instanceof Array ? value[index] : value;
  }
  return newObject;
}

/**
 * This class holds a value of the type T to be wrapped with helpers
 * methods implemented by the subclasses that extend this class.
 */
class Value<T> {
  constructor(readonly value: T) {}
}

export class StringValue extends Value<string> {
  toFloat(): number {
    return parseStringToFloat(this.value);
  }

  toInt(): number {
    return parseStringToInt(this.value);
  }

  trim(): StringValue {
    return new StringValue(this.value.trim());
  }

  /**
   * Match text using regex within the element
   * @param regexp: Regular Expression used to match text
   */
  matchOne(regexp: RegExp): StringValue | null {
    // use regexp.exec instead of value.match to ensure only one result is returned.
    // String.match can return a group of matches if 'g' flag is specified
    const result = regexp.exec(this.value);
    if (result === null) return null;
    return new StringValue(result[0]);
  }

  replace(searchValue: string | RegExp, replaceValue: string): StringValue {
    return new StringValue(this.value.replace(searchValue, replaceValue));
  }
}

type HTMLElementType = HTMLElement | null;

/**
 * This class wraps an object HTMLElement to allow easier manipulation
 * of the element with a set of commons utility methods.
 */
export class WrappedHTMLElement {
  readonly element: HTMLElementType;

  /**
   * Creates a new WrappedHTMLElement. This constructor allows to wrap an HTMLElement, or
   * to wrap a child of this element, selected by the optional parameter {selector}
   * @param targetElement
   * @param selector
   */
  constructor(targetElement: Document | HTMLElementType, selector?: string | null, matchingText?: string | null) {
    if (targetElement) {
      let collection: HTMLElement[];
      if (selector) collection = Array.from(targetElement.querySelectorAll<HTMLElement>(selector));
      else collection = targetElement instanceof Document ? [] : [targetElement];
      if (matchingText) this.element = this.filterElementsByText(collection, matchingText);
      else this.element = collection.length > 0 ? collection[0] : null;
    } else this.element = null;
  }

  /**
   * Iterates through the list of possible attributes of the wrapped element and return the first with a value not null.
   * @param attrsNames {string}
   * @returns {StringValue|null}
   */
  findAttr(attrsNames: string[]): StringValue | null {
    for (const attrName of attrsNames) {
      const attrValue = this.getAttr(attrName);
      if (attrValue) return attrValue;
    }
    return null;
  }

  getAttr(attrName: string): StringValue | null {
    if (!this.element) return null;
    let attrValue = this.element.getAttribute(attrName);
    if (attrValue) return new StringValue(attrValue);
    attrValue = this.element.getAttribute(`data-${attrName}`);
    if (attrValue) return new StringValue(attrValue);
    return null;
  }

  /**
   * Search through the class names on the element, and return the first class name to match the prefix.
   * The prefix will be removed from the value before it is returned.
   * @param prefix: prefix to search the class list for
   */
  searchClassListByPrefix(prefix: string): StringValue | null {
    if (!this.element) return null;
    const results = [...this.element.classList].filter((className) => className.startsWith(prefix));
    return results.length > 0 ? new StringValue(results[0].replace(prefix, '')) : null;
  }

  getTextContent(): StringValue | null {
    if (!this.element) return null;
    const textContent = this.element.textContent;
    if (textContent) return new StringValue(textContent.trim());
    else return null;
  }

  nextSibling(): WrappedHTMLElement | null {
    if (!this.element) return null;
    const sibling = this.element.nextElementSibling;
    if (sibling) return new WrappedHTMLElement(<HTMLElement>sibling);
    else return null;
  }

  previousSibling(): WrappedHTMLElement | null {
    if (!this.element) return null;
    const sibling = this.element.previousElementSibling;
    if (sibling) return new WrappedHTMLElement(<HTMLElement>sibling);
    else return null;
  }

  /**
   * Get a child element based on a selector
   * @param selector: Selector string to pass to querySelector
   */
  childBySelector(selector: string): WrappedHTMLElement | null {
    const child = this.element?.querySelector<HTMLElement>(selector);
    if (child) return new WrappedHTMLElement(child);
    else return null;
  }

  filterElementsByText(collection: HTMLElement[], text: string): HTMLElement | null {
    for (const element of collection) {
      if (element.textContent && element.textContent.trim().toLowerCase() == text.trim().toLowerCase()) return element;
    }
    return null;
  }

  parent(): WrappedHTMLElement | null {
    if (!this.element) return null;
    const parent = this.element.parentElement;
    if (parent) return new WrappedHTMLElement(parent);
    else return null;
  }
}
